import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminSidebar from '../components/AdminSidebar';
import { useNavigate } from 'react-router-dom';
import { FaChartBar, FaBoxOpen, FaUserCheck, FaUsers, FaSync, FaPlus, FaEdit, FaTrash, FaEnvelope, FaInfoCircle, FaPhone } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const INIT_PRODUCT = { _id: null, name: '', nameMarathi: '', category: '', price: '', description: '', image: '', specifications: '', quantity: '', inStock: true };
const INIT_REPLY = { subject: '', message: '' };
const TABS = [
  { id: 'overview', icon: <FaChartBar />, label: 'Overview' },
  { id: 'contacts', icon: <FaUsers />, label: 'Contacts' },
  { id: 'products', icon: <FaBoxOpen />, label: 'Products' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalContacts: 0, pendingContacts: 0, totalProducts: 0, recentContacts: 0 });
  
  // Product modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [productForm, setProductForm] = useState(INIT_PRODUCT);
  const [addingProduct, setAddingProduct] = useState(false);
  
  // Reply modal state
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyForm, setReplyForm] = useState(INIT_REPLY);
  const [replyingToContact, setReplyingToContact] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  
  const API = process.env.REACT_APP_API_BASE_URL;

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsRes, productsRes] = await Promise.all([
        axios.get(`${API}/contact/get-all`),
        axios.get(`${API}/products/get-products`)
      ]);
      setContacts(contactsRes.data);
      setProducts(productsRes.data);
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const recentContactsCount = contactsRes.data.filter(c => new Date(c.createdAt) > sevenDaysAgo).length;
      setStats({
        totalContacts: contactsRes.data.length,
        pendingContacts: contactsRes.data.filter(c => c.status === 'pending').length,
        totalProducts: productsRes.data.length,
        recentContacts: recentContactsCount
      });
    } catch (error) {
      toast.error('Dashboard fetch error. Check your connection or login state.');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [API, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchDashboardData();
  }, [navigate, API, fetchDashboardData]);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Product deleted!');
      fetchDashboardData();
    } catch (e) {
      toast.error('Failed to delete.');
    }
  };

  // Product form handlers
  const openProductModal = (product = null) => {
    setProductForm(product || INIT_PRODUCT);
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setProductForm(INIT_PRODUCT);
  };

  const handleProductChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setAddingProduct(true);
    try {
      if (!productForm.name || !productForm.category || !productForm.price || !productForm.description) {
        toast.error('Required fields missing');
        setAddingProduct(false);
        return;
      }
      if (isNaN(+productForm.price) || +productForm.price < 1) {
        toast.error('Enter a valid price');
        setAddingProduct(false);
        return;
      }
      if (productForm._id) {
        await axios.put(`${API}/products/${productForm._id}`, { 
          ...productForm, 
          price: parseFloat(productForm.price), 
          quantity: parseInt(productForm.quantity || '0') 
        });
        toast.success('Product updated!');
      } else {
        await axios.post(`${API}/products`, { 
          ...productForm, 
          price: parseFloat(productForm.price), 
          quantity: parseInt(productForm.quantity || '0') 
        });
        toast.success('Product added!');
      }
      fetchDashboardData();
      closeProductModal();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Product add/update failed.');
    } finally {
      setAddingProduct(false);
    }
  };

  // Reply form handlers
  const openReplyModal = (contact) => {
    setReplyingToContact(contact);
    setReplyForm({ 
      subject: `Re: ${contact.service || 'Your Inquiry'} - Kanhaiya Krushi`, 
      message: `Hello ${contact.name},\n\n` 
    });
    setShowReplyModal(true);
  };

  const closeReplyModal = () => {
    setShowReplyModal(false);
    setReplyForm(INIT_REPLY);
    setReplyingToContact(null);
  };

  const handleReplyChange = (e) => {
    const { name, value } = e.target;
    setReplyForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    setSendingReply(true);
    try {
      if (!replyingToContact) return;
      if (!replyForm.message || !replyForm.subject) {
        toast.error('Subject and message required');
        setSendingReply(false);
        return;
      }
      await axios.post(`${API}/contact/${replyingToContact._id}/reply`, { message: replyForm.message });
      toast.success('Reply sent!');
      fetchDashboardData();
      closeReplyModal();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send reply.');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <div className="flex-1 bg-green-50">
        {/* Sticky Dashboard Header */}
        <div className="sticky top-0 z-10 bg-green-50 border-b border-green-200 flex items-center px-6 py-4 shadow-sm">
          <span className="text-2xl font-bold text-green-900 mr-8 tracking-wider">Admin Dashboard</span>
          <div className="flex space-x-1 bg-green-100 rounded-xl p-1 shadow">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center space-x-2 px-5 py-2 rounded-xl font-semibold transition-all duration-200 text-lg ${activeTab === tab.id ? 'bg-green-700 text-white shadow' : 'bg-green-100 text-green-800 hover:bg-white hover:text-green-900'}`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
          <button onClick={fetchDashboardData} className="ml-auto flex items-center px-4 py-2 rounded-xl bg-white text-green-700 font-semibold border border-green-200 shadow hover:bg-green-100">
            <FaSync className="mr-2" /> Refresh
          </button>
        </div>

        {/* Loading spinner */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
          </div>
        )}

        {!loading && (
        <div className="px-6 py-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center space-x-4 border-t-4 border-blue-300">
                <FaUsers className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-md text-gray-500">Total Contacts</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.totalContacts}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center space-x-4 border-t-4 border-yellow-300">
                <FaUserCheck className="w-8 h-8 text-yellow-600" />
                <div>
                  <p className="text-md text-gray-500">Pending Contacts</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.pendingContacts}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center space-x-4 border-t-4 border-green-400">
                <FaBoxOpen className="w-8 h-8 text-green-700" />
                <div>
                  <p className="text-md text-gray-500">Total Products</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.totalProducts}</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 flex items-center space-x-4 border-t-4 border-purple-400">
                <FaInfoCircle className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-md text-gray-500">Recent Contacts (7d)</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.recentContacts}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <>
              <div className="mb-5 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-green-900">Contact Inquiries</h2>
              </div>
              <div className="bg-white rounded-2xl shadow-sm p-6 overflow-x-auto">
                <table className="min-w-full divide-y divide-green-200">
                  <thead>
                    <tr className="bg-green-100">
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Message</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact, i) => (
                      <tr key={contact._id} className={`hover:bg-green-50 ${i%2 ? 'bg-green-50/60' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{contact.name}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {contact.phone}<br />
                          {contact.email && <span className="text-xs text-gray-400">{contact.email}</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{contact.service || 'N/A'}</td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{contact.message}</td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(contact.createdAt)}</td>
                        <td className="px-4 py-3">
                          <select
                            value={contact.status}
                            onChange={async e => {
                              try {
                                await axios.put(`${API}/contact/${contact._id}`, { status: e.target.value });
                                fetchDashboardData();
                              } catch {
                                toast.error('Failed to update status.');
                              }
                            }}
                            className="border border-gray-300 rounded px-2 py-1 text-xs"
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="resolved">Resolved</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 flex gap-1">
                          {contact.email ? (
                            <button 
                              className="bg-blue-50 text-blue-700 p-2 rounded hover:bg-blue-100 border border-blue-100" 
                              title="Reply via Email"
                              onClick={() => openReplyModal(contact)}
                            >
                              <FaEnvelope />
                            </button>
                          ) : (
                            <button 
                              className="bg-green-50 text-green-700 p-2 rounded hover:bg-green-100 border border-green-100" 
                              title="Call this Contact"
                              onClick={() => window.open(`tel:${contact.phone}`, "_self")}
                            >
                              <FaPhone />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {contacts.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-green-400">No contacts yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'products' && (
            <>
              <div className="mb-5 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-green-900">Products Management</h2>
                <button onClick={() => openProductModal()} className="bg-green-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-green-800 shadow">
                  <FaPlus /> Add Product
                </button>
              </div>
              <div className="bg-white rounded-2xl shadow-sm p-6 overflow-x-auto border border-green-100">
                <table className="min-w-full divide-y divide-green-200">
                  <thead>
                    <tr className="bg-green-100">
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Stock</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-green-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, i) => (
                      <tr key={product._id} className={`hover:bg-green-50 ${i%2 ? 'bg-green-50/60' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{product.name}</td>
                        <td className="px-4 py-3 text-gray-600">{product.category}</td>
                        <td className="px-4 py-3 text-green-700 font-bold">₹{product.price}</td>
                        <td className="px-4 py-3">
                          {product.inStock ? (
                            <span className="bg-green-200 text-green-900 rounded px-2 py-1 text-xs font-semibold">In Stock</span>
                          ) : (
                            <span className="bg-red-200 text-red-900 rounded px-2 py-1 text-xs font-semibold">Out</span>
                          )}
                        </td>
                        <td className="px-4 py-3 flex gap-2">
                          <button className="bg-blue-50 text-blue-700 p-2 rounded hover:bg-blue-100 border border-blue-100" onClick={() => openProductModal(product)} title="Edit"><FaEdit /></button>
                          <button className="bg-red-50 text-red-700 p-2 rounded hover:bg-red-100 border border-red-100" onClick={() => deleteProduct(product._id)} title="Delete"><FaTrash /></button>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 && (
                      <tr><td colSpan={5} className="py-8 text-center text-green-400">No products yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        )}

        {/* Product Modal */}
        {showProductModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">
                  {productForm._id ? "Edit Product" : "Add Product"}
                </h2>
                <button
                  onClick={closeProductModal}
                  className="text-white hover:text-green-100 text-2xl font-bold"
                  type="button"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleProductSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 font-medium">Name (English) *</label>
                    <input
                      type="text"
                      name="name"
                      value={productForm.name}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Name (Marathi)</label>
                    <input
                      type="text"
                      name="nameMarathi"
                      value={productForm.nameMarathi}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Category *</label>
                    <select
                      name="category"
                      value={productForm.category}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="Seeds">Seeds</option>
                      <option value="Fertilizers">Fertilizers</option>
                      <option value="Pesticides">Pesticides</option>
                      <option value="Tools">Tools</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Price (₹) *</label>
                    <input
                      type="number"
                      name="price"
                      value={productForm.price}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                      required
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Quantity</label>
                    <input
                      type="number"
                      name="quantity"
                      value={productForm.quantity}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Image URL</label>
                    <input
                      type="url"
                      name="image"
                      value={productForm.image}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Description *</label>
                    <textarea
                      name="description"
                      value={productForm.description}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                      required
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Specifications</label>
                    <textarea
                      name="specifications"
                      value={productForm.specifications}
                      onChange={handleProductChange}
                      className="w-full px-3 py-2 border rounded"
                      rows="1"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={productForm.inStock}
                      onChange={handleProductChange}
                    />
                    <span>In Stock</span>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeProductModal}
                    className="px-6 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-semibold"
                    disabled={addingProduct}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={addingProduct}
                  >
                    {addingProduct ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reply Modal */}
        {showReplyModal && replyingToContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">
                  Send Reply to {replyingToContact.name}
                </h2>
                <button
                  onClick={closeReplyModal}
                  className="text-white hover:text-green-100 text-2xl font-bold"
                  type="button"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleReplySubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 font-medium">To</label>
                    <input
                      type="email"
                      value={replyingToContact.email}
                      className="w-full px-3 py-2 border rounded bg-gray-100"
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={replyForm.subject}
                      onChange={handleReplyChange}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium">Message *</label>
                    <textarea
                      name="message"
                      required
                      value={replyForm.message}
                      onChange={handleReplyChange}
                      className="w-full px-3 py-2 border rounded"
                      rows="8"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeReplyModal}
                    className="px-6 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-semibold"
                    disabled={sendingReply}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={sendingReply}
                  >
                    {sendingReply ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ToastContainer position="top-center" autoClose={2600} hideProgressBar />
      </div>
    </div>
  );
};

export default AdminDashboard;
